import { VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import {
  useIsTipoUnidadeSaudeAllowedAsReferenciaVinculacaoServicoQuery,
  useListaCuidadoCompartilhadoQuery,
  useVisualizarTodosCuidadosCompartilhadosMutation,
} from 'graphql/hooks.generated'
import { Action, CuidadoCompartilhadoQueryFilterEnum, CuidadosCompartilhadoQueryInput } from 'graphql/types.generated'
import useAtmosphere from 'hooks/useAtmosphere'
import { debounce, isEqual } from 'lodash'
import React, { useEffect, useState } from 'react'

import {
  CuidadoCompartilhadoItemActions,
  CuidadoCompartilhadoSortEnum,
  ListaCuidadoCompartilhadoFilterModel,
  ListaCuidadoCompartilhadoInlineFilterModel,
} from '../model-cuidadoCompartilhado'
import { ListaCuidadoCompartilhadoFilter } from './lista-cuidado-compartilhado-filter/ListaCuidadoCompartilhadoFilter'
import ListaCuidadoCompartilhadoTable from './lista-cuidado-compartilhado-table/ListaCuidadoCompartilhadoTable'

export interface CuidadoCompartilhadoAtmosphereResponse {
  idProfissionalSolicitante: string
  idEquipeSolicitante: string
  idProfissionalExecutante: string
  idEquipeExecutante: string
  idEstabelecimentoExecutante: string
}

interface ListaCuidadoCompartilhadoViewProps {
  filterDefault: ListaCuidadoCompartilhadoFilterModel
  filterSwitchLabel?: string
  itemsToSort: CuidadoCompartilhadoSortEnum[]
  initialSort: CuidadoCompartilhadoSortEnum
  somenteSemExecutante?: boolean
  role: CuidadoCompartilhadoQueryFilterEnum
  filterToQueryInput(value: ListaCuidadoCompartilhadoFilterModel): CuidadosCompartilhadoQueryInput
  resolveDiscutirAction?(actions: CuidadoCompartilhadoItemActions): Action
}

export const ListaCuidadoCompartilhadoView = (props: ListaCuidadoCompartilhadoViewProps) => {
  const {
    filterDefault,
    initialSort,
    itemsToSort,
    filterSwitchLabel,
    role,
    somenteSemExecutante,
    filterToQueryInput,
    resolveDiscutirAction = () => null,
  } = props
  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const estabelecimento = acesso.unidadeSaude
  const [isFilterDefault, setIsFilterDefault] = useState(true)
  const [filter, setFilter] = useState<ListaCuidadoCompartilhadoFilterModel>({
    ...filterDefault,
    pageParams: {
      size: 5,
      page: 0,
      sort: initialSort,
    },
  })

  const [visualizarTodosCuidadosCompartilhados] = useVisualizarTodosCuidadosCompartilhadosMutation()
  const { data, loading, refetch } = useListaCuidadoCompartilhadoQuery({
    variables: { input: filterToQueryInput(filter) },
    onCompleted: () => {
      visualizarTodosCuidadosCompartilhados()
    },
  })

  const {
    data: isVinculacaoServicoAllowed,
    loading: loadingVinculacaoServicoAllowed,
  } = useIsTipoUnidadeSaudeAllowedAsReferenciaVinculacaoServicoQuery({
    variables: { tipoCoMs: estabelecimento.tipo.codigoMs },
    fetchPolicy: 'cache-first',
  })

  const isEstabelecimentoEspecializado =
    !loadingVinculacaoServicoAllowed &&
    isVinculacaoServicoAllowed?.isTipoUnidadeSaudeAllowedAsReferenciaVinculacaoServico

  const onAtmosphereMessage = (response: CuidadoCompartilhadoAtmosphereResponse) => {
    const estabelecimentoMatches =
      isVinculacaoServicoAllowed && response.idEstabelecimentoExecutante === estabelecimento.id
    const profissinalSolicitanteMatches = response.idProfissionalSolicitante === profissional.id
    const profissinalExecutanteMatches = response.idProfissionalExecutante === profissional.id
    const equipeFieldsNotNull = !!acesso.equipe && (!!response.idEquipeSolicitante || !!response.idEquipeExecutante)
    const equipeSolicitanteMatches = equipeFieldsNotNull && response.idEquipeSolicitante === acesso.equipe?.id
    const equipeExecutanteMatches = equipeFieldsNotNull && response.idEquipeExecutante === acesso.equipe?.id

    if (
      profissinalSolicitanteMatches ||
      profissinalExecutanteMatches ||
      equipeSolicitanteMatches ||
      equipeExecutanteMatches ||
      estabelecimentoMatches
    ) {
      debounce(() => {
        refetch()
        visualizarTodosCuidadosCompartilhados()
      }, 350)()
    }
  }

  useAtmosphere<CuidadoCompartilhadoAtmosphereResponse>({
    topic: `cuidadocompartilhado`,
    onMessage: onAtmosphereMessage,
    onConnect: () => {},
  })

  const onFilterChange = (value: ListaCuidadoCompartilhadoInlineFilterModel) => {
    setFilter((oldValue: ListaCuidadoCompartilhadoFilterModel) => ({
      ...oldValue,
      ...value,
    }))
  }

  useEffect(() => {
    setIsFilterDefault(isFilterEqualFilterDefault(filter, filterDefault))
  }, [filter, filterDefault])

  return (
    <VFlow vSpacing={0.5}>
      <ListaCuidadoCompartilhadoFilter
        value={filter}
        itemsToSort={itemsToSort}
        filterSwitchLabel={filterSwitchLabel}
        isFilterDefault={isFilterDefault}
        isEstabelecimentoEspecializado={isEstabelecimentoEspecializado}
        role={role}
        somenteSemExecutante={somenteSemExecutante}
        onChange={onFilterChange}
        onClear={() => onFilterChange(filterDefault)}
      />
      <ListaCuidadoCompartilhadoTable
        loading={loading || loadingVinculacaoServicoAllowed}
        setFilter={setFilter}
        isGerente={role === CuidadoCompartilhadoQueryFilterEnum.GERENTE}
        queryPayload={data?.cuidadosCompartilhado}
        resolveDiscutirAction={resolveDiscutirAction}
      />
    </VFlow>
  )
}

const isFilterEqualFilterDefault = (
  filter: ListaCuidadoCompartilhadoFilterModel,
  filterDefault: ListaCuidadoCompartilhadoFilterModel
): boolean => {
  const { pageParams, query, ...rest } = filter

  return isEqual(
    {
      status: (rest.status ?? []).sort(),
      periodo: rest.periodo ?? null,
      profissionalExecutante: rest.profissionalExecutante ?? null,
      profissionalSolicitante: rest.profissionalSolicitante ?? null,
      unidadeSaude: rest.unidadeSaude ?? null,
      equipe: (rest.equipe ?? []).sort(),
      switch: rest.switch,
      cbo: rest.cbo,
    },
    filterDefault
  )
}
